<template>
  <div class="section-login">
    <div class="login-component w-layout-grid">
      <!-- Left Content Area -->
      <div class="login-content-left">
        <!-- Logo -->
        <div class="login-navbar">
          <router-link to="/" class="logo-link">
            <img :src="'/media/recraft/logo/denvstack-icon.svg'" alt="DenvStack" class="logo-icon" />
            <span>DenvStack</span>
          </router-link>
        </div>

        <!-- Form Content -->
        <div class="login-form-block">
          <h1 class="login-title">Reset Password</h1>

          <!-- Success Message -->
          <div v-if="showSuccess" class="success-message w-form-done">
            <div>Password has been reset successfully. You can now login with your new password.</div>
            <div class="redirect-info">Redirecting to login in {{ countdown }} seconds...</div>
          </div>

          <!-- Error Message -->
          <div v-else-if="serverError" class="error-message w-form-fail">
            <div>{{ errorTitle }}: {{ errorDescription }}</div>
            <div class="error-actions">
              <button @click="$router.push('/')" class="button max-width-full w-button">
                Go Home
              </button>
              <button @click="requestNewReset" class="button max-width-full w-button" style="margin-top: 10px;">
                Request New Link
              </button>
            </div>
          </div>

          <!-- Reset Form -->
          <form v-else @submit.prevent="resetPassword" class="login-form w-form">
            <div v-if="errorText" class="error-message w-form-fail">
              {{ errorText }}
            </div>

            <div class="form-field-wrapper">
              <label for="newPassword" class="field-label">New Password*</label>
              <input
                id="newPassword"
                v-model="password"
                type="password"
                required
                :disabled="loading"
                placeholder="Enter your new password"
                class="form-input w-input"
                minlength="8"
                @input="clearError"
              >
              <div v-if="password && !isPasswordStrong" class="password-requirements">
                Password must contain:
                <ul>
                  <li :class="{ met: password.length >= 8 }">At least 8 characters</li>
                  <li :class="{ met: /[A-Z]/.test(password) }">One uppercase letter</li>
                  <li :class="{ met: /[a-z]/.test(password) }">One lowercase letter</li>
                  <li :class="{ met: /\d/.test(password) }">One number</li>
                </ul>
              </div>
            </div>

            <div class="form-field-wrapper">
              <label for="confirmPassword" class="field-label">Confirm New Password*</label>
              <input
                id="confirmPassword"
                v-model="confirmPassword"
                type="password"
                required
                :disabled="loading"
                placeholder="Confirm your new password"
                class="form-input w-input"
                minlength="8"
                @input="clearError"
              >
            </div>

            <div v-if="password && confirmPassword && !passwordsMatch" class="error-message w-form-fail">
              Passwords do not match
            </div>

            <input
              type="submit"
              :disabled="loading || !isFormValid"
              :value="loading ? 'Please wait...' : 'Submit'"
              class="button max-width-full w-button"
            >
          </form>

          <!-- Footer -->
          <div class="login-footer">
            <p>
              Remember your password?
              <router-link to="/" class="auth-link">Back to Home</router-link>
            </p>
          </div>
        </div>
      </div>

      <!-- Right Image Area -->
      <div class="login-image-wrapper">
        <div class="login-background-gradient">
          <img :src="'/media/recraft/logo/denvstack-icon-dark.svg'" alt="DenvStack" class="login-background-icon" />
          <h2>DenvStack</h2>
          <p>AI-Native Development Platform</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authService } from '@/services/auth-api'

export default {
  name: 'ResetPassword',
  data() {
    return {
      username: '',
      hash: '',
      password: '',
      confirmPassword: '',
      loading: false,
      showSuccess: false,
      errorText: '',
      serverError: false,
      errorTitle: 'Something went wrong',
      errorDescription: 'Please try again later.',
      countdown: 3
    }
  },
  computed: {
    passwordsMatch() {
      return this.password === this.confirmPassword
    },
    isFormValid() {
      return this.password && 
             this.confirmPassword && 
             this.passwordsMatch && 
             this.isPasswordStrong
    },
    isPasswordStrong() {
      // Password must be at least 8 characters with uppercase, lowercase, and number
      const hasMinLength = this.password.length >= 8
      const hasUpperCase = /[A-Z]/.test(this.password)
      const hasLowerCase = /[a-z]/.test(this.password)
      const hasNumbers = /\d/.test(this.password)
      
      return hasMinLength && hasUpperCase && hasLowerCase && hasNumbers
    }
  },
  created() {
    // Extract user and hash from route query params
    this.username = this.$route.query.user
    this.hash = this.$route.query.hash

    // Validate required parameters
    if (!this.username || !this.hash) {
      this.serverError = true
      this.errorTitle = 'Invalid Reset Link'
      this.errorDescription = 'The password reset link is invalid or incomplete. Please request a new password reset link.'
      return
    }

    // Validate hash format (should be hex string, typically 40 or 64 characters)
    if (!/^[a-f0-9]{40,64}$/i.test(this.hash)) {
      this.serverError = true
      this.errorTitle = 'Invalid Reset Token'
      this.errorDescription = 'The password reset token is invalid or has been corrupted. Please request a new password reset link.'
      return
    }
  },
  methods: {
    clearError() {
      this.errorText = ''
      this.serverError = false
    },

    async resetPassword() {
      this.clearError()
      this.loading = true

      try {
        // Use the authService resetPassword method
        const response = await authService.resetPassword({
          username: this.username,
          hash: this.hash,
          newPassword: this.password
        })

        this.loading = false
        
        if (response.status === true) {
          this.showSuccess = true
          
          // Show notification
          this.$store.dispatch('showNotification', {
            type: 'success',
            message: 'Password reset successfully! Redirecting to login...'
          })
          
          // Start countdown and redirect
          this.startCountdown()
        } else {
          this.errorText = response.message || 'Failed to reset password. Please try again.'
        }
        
      } catch (error) {
        console.error('Password reset error:', error)
        this.loading = false
        
        if (error.response?.status >= 500) {
          this.serverError = true
          this.errorTitle = 'Server Error'
          this.errorDescription = 'Our servers are temporarily unavailable. Please try again in a few minutes.'
        } else if (error.response?.status === 400) {
          this.serverError = true
          this.errorTitle = 'Invalid Reset Link'
          this.errorDescription = 'This password reset link is no longer valid. Please request a new one.'
        } else if (error.response?.status === 401) {
          this.serverError = true
          this.errorTitle = 'Link Expired'
          this.errorDescription = 'This password reset link has expired for security reasons. Please request a new one.'
        } else {
          this.errorText = error.response?.data?.message || 'An error occurred while resetting your password.'
        }
        
        this.$store.dispatch('showNotification', {
          type: 'error',
          message: this.errorText
        })
      }
    },

    startCountdown() {
      const timer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          clearInterval(timer)
          // Redirect to home page and show auth modal
          this.$router.push('/')
          setTimeout(() => {
            this.$store.commit('setShowAuthModal', true)
          }, 500)
        }
      }, 1000)
    },

    requestNewReset() {
      // Navigate to home page and show auth modal with forgot password
      this.$router.push('/')
      setTimeout(() => {
        this.$store.commit('setShowAuthModal', true)
        this.$store.commit('setAuthModalMode', 'forgot-password')
      }, 500)
    }
  }
}
</script>

<style scoped>
/* Split Screen Layout - matches default-webapp */
.section-login {
  height: 100vh;
  width: 100%;
  display: flex;
  overflow: hidden;
}

.w-layout-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  height: 100%;
}

/* Left Content Area */
.login-content-left {
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 40px;
  overflow-y: auto;
}

.login-navbar {
  margin-bottom: 20px;
}

.logo-link {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: #333;
  font-size: 1.5rem;
  font-weight: 700;
}

.logo-icon {
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.login-form-block {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 400px;
  margin: 0 auto;
  width: 100%;
}

.login-title {
  font-size: 2rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 2rem;
  text-align: left;
}

/* Form Styling - matches default-webapp */
.login-form {
  width: 100%;
}

.form-field-wrapper {
  margin-bottom: 20px;
}

.field-label {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  font-size: 0.875rem;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;
  background-color: #fff;
}

.form-input:focus {
  outline: none;
  border-color: #1a7fd4;
  box-shadow: 0 0 0 2px rgba(26, 127, 212, 0.1);
}

.form-input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.w-input {
  /* Additional Webflow-style input classes */
}

/* Button Styling - matches default-webapp */
.button {
  background-color: #1a7fd4;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  text-align: center;
}

.button:hover:not(:disabled) {
  background-color: #0d6ebd;
}

.button:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.max-width-full {
  width: 100%;
}

.w-button {
  /* Additional Webflow-style button classes */
}

/* Success and Error Messages - matches default-webapp */
.success-message {
  background-color: #a1ceceab;
  color: #065f46;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  text-align: center;
}

.w-form-done {
  /* Additional Webflow success styling */
}

.error-message {
  background-color: #ffdede;
  color: #dc2626;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  text-align: center;
}

.w-form-fail {
  /* Additional Webflow error styling */
}

.redirect-info {
  margin-top: 10px;
  font-size: 0.875rem;
  color: #6b7280;
}

.error-actions {
  margin-top: 15px;
}

/* Password Requirements */
.password-requirements {
  margin-top: 8px;
  padding: 12px;
  background-color: #fef3c7;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #92400e;
}

.password-requirements ul {
  margin: 8px 0 0 0;
  padding-left: 20px;
  list-style: none;
}

.password-requirements li {
  margin: 4px 0;
  position: relative;
  color: #dc2626;
}

.password-requirements li:before {
  content: "✗";
  position: absolute;
  left: -16px;
  color: #dc2626;
}

.password-requirements li.met {
  color: #059669;
}

.password-requirements li.met:before {
  content: "✓";
  color: #059669;
}

/* Footer */
.login-footer {
  margin-top: 20px;
  text-align: center;
  color: #666;
  font-size: 0.875rem;
}

.auth-link {
  color: #1a7fd4;
  text-decoration: none;
  font-weight: 600;
}

.auth-link:hover {
  text-decoration: underline;
}

/* Right Image Area */
.login-image-wrapper {
  background: #0a2540;
  background-image: url('/media/recraft/textures/texture-grid.svg');
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.login-image-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 37, 64, 0.85);
  z-index: 0;
}

.login-background-gradient {
  text-align: center;
  color: white;
  padding: 40px;
  position: relative;
  z-index: 1;
}

.login-background-icon {
  width: 100px;
  height: 100px;
  margin-bottom: 20px;
  object-fit: contain;
}

.login-background-gradient h2 {
  font-size: 2rem;
  margin-bottom: 10px;
  font-weight: 600;
}

.login-background-gradient p {
  font-size: 1.125rem;
  opacity: 0.9;
}

/* Responsive Design */
@media (max-width: 991px) {
  .w-layout-grid {
    grid-template-columns: 1fr;
  }
  
  .login-image-wrapper {
    display: none;
  }
  
  .login-content-left {
    padding: 30px;
  }
}

@media (max-width: 767px) {
  .login-content-left {
    padding: 20px;
  }
  
  .login-title {
    font-size: 1.75rem;
  }
}

@media (max-width: 479px) {
  .login-content-left {
    padding: 15px;
  }
  
  .login-title {
    font-size: 1.5rem;
  }
}
</style>